import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/Login.css";
import { useUser } from "../contexts/UserContext";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../authConfig";
import { useGoogleLogin } from "@react-oauth/google";

/**
 * @typedef {Object} LoginResponse
 * @property {boolean} success
 * @property {string} [error]
 * @property {Object} [user]
 * @property {number} user.id
 * @property {string} user.name
 * @property {string} user.email
 * @property {string} user.role
 * @property {string} [user.clientId]
 * @property {string} [user.clientName]
 * @property {string} [user.databaseName]
 */

function Login() {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { login, isLoggedIn, user } = useUser();
  const { instance } = useMsal();


  // Redirect hvis allerede logget ind
  useEffect(() => {
    // Ryd tidligere service valg ved login
    localStorage.removeItem("selectedService");

    if (isLoggedIn) {
      if (user.role === 'admin') {
        navigate('/admin');
      } else if (user.role === 'consultant') {
        navigate('/clients');
      } else {
        navigate(`/client/${user.clientId}/dataexchange`);
      }
    }
  }, [isLoggedIn, navigate, user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Nulstil fejlmeddelelse når bruger begynder at skrive
    if (error) setError('');
  };

  const handleLogin = async () => {
    try {
      setIsLoading(true);
      await instance.loginPopup({
        ...loginRequest,
        prompt: "select_account",
        redirectUri: process.env.REACT_APP_POPUP_REDIRECT_URI,
      });

      const account = instance.getActiveAccount();
      setError("");
      const apiUrl = `${
        process.env.REACT_APP_API_URL || "http://localhost:5000/api"
      }/auth/verify-token`;
      try {
        const response = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            userName: account.name,
            userEmail: account.username,
          }),
        });

        if (!response.ok) {
          const errorData = await response.text();
          throw new Error(errorData);
        }

        /** @type {LoginResponse} */
        const data = await response.json();

        if (data.success && data.user) {
          // Brug UserContext's login funktion med token
          login(data.user, data.token);
          
          // Redirect baseret på brugerens rolle
          if (data.user.firstLogin) {
            navigate('/change-password');
          } else if (data.user.role === 'admin') {
            navigate('/admin');
          } else if (data.user.role === 'consultant') {
            navigate('/clients');
          } else {
            navigate(`/client/${data.user.clientId}/dataexchange`);
          }
        } else {
          throw new Error("Ugyldig login respons");
        }
      } catch (err) {
        try {
          const jsonError = JSON.parse(err.message);
          setError(jsonError.error || 'Der opstod en fejl under login');
        } catch (e) {
          setError(err.message || 'Der opstod en fejl under login');
        }
      } finally {
        setIsLoading(false);
      }
    } catch (err) {
      console.log(err);
      setIsLoading(false);
    }
  };

  const handleGoogleSuccess = async (res) => {
    setIsLoading(true);
    setError("");
    const apiUrl = `${
      process.env.REACT_APP_API_URL || "http://localhost:5000/api"
    }/auth/verify-token`;
    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          token: res?.access_token,
          authName: "google",
        }),
      });

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(errorData);
      }

      /** @type {LoginResponse} */
      const data = await response.json();

      if (data.success && data.user) {
        // Brug UserContext's login funktion med token
        login(data.user, data.token);
        
        // Redirect baseret på brugerens rolle
        if (data.user.firstLogin) {
          navigate('/change-password');
        } else if (data.user.role === 'admin') {
          navigate('/admin');
        } else if (data.user.role === 'consultant') {
          navigate('/clients');
        } else {
          navigate(`/client/${data.user.clientId}/dataexchange`);
        }
      } else {
        throw new Error("Ugyldig login respons");
      }
    } catch (err) {
      try {
        const jsonError = JSON.parse(err.message);
        setError(jsonError.error || 'Der opstod en fejl under login');
      } catch (e) {
        setError(err.message || 'Der opstod en fejl under login');
      }
    } finally {
      setIsLoading(false);
    }
  };
  const handleFailure = () => {
    console.log("error");
  };
  const googleLogin = useGoogleLogin({
    onSuccess: (credentialResponse) => {
      handleGoogleSuccess(credentialResponse);
    },
    onError: () => {
      handleFailure();
    },
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    const apiUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/auth/login`;
    
    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: formData.email, password: formData.password })
      });
      
      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(errorData);
      }

      /** @type {LoginResponse} */
      const data = await response.json();

      if (data.success && data.user) {
        // Brug UserContext's login funktion med token
        login(data.user, data.token);
        
        // Redirect baseret på brugerens rolle
        if (data.user.firstLogin) {
          navigate('/change-password');
        } else if (data.user.role === 'admin') {
          navigate('/admin');
        } else if (data.user.role === 'consultant') {
          navigate('/clients');
        } else {
          navigate(`/client/${data.user.clientId}/dataexchange`);
        }
      } else {
        throw new Error('Ugyldig login respons');
      }
    } catch (err) {
      try {
        const jsonError = JSON.parse(err.message);
        setError(jsonError.error || 'Der opstod en fejl under login');
      } catch (e) {
        setError(err.message || 'Der opstod en fejl under login');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleForgotPassword = () => {
    navigate("/forgot-password");
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <div className="login-header">
          <img
            src="/logo.png"
            alt="Din Lønpartner Logo"
            className="login-logo"
          />
          <h1>Log ind</h1>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && (
            <div className="error-message" role="alert">
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <div className="input-with-icon">
              <i className="fas fa-envelope"></i>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Indtast din email"
                autoComplete="email"
                required
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="password">Adgangskode</label>
            <div className="input-with-icon">
              <i className="fas fa-lock"></i>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Indtast din adgangskode"
                autoComplete="current-password"
                required
                disabled={isLoading}
              />
            </div>
          </div>

          <button type="submit" className="login-button" disabled={isLoading}>
            {isLoading ? (
              <span className="loading-spinner">
                <i className="fas fa-spinner fa-spin"></i>
              </span>
            ) : (
              "Log ind"
            )}
          </button>

          <div className="forgot-password">
            <a
              href="#glemt-adgangskode"
              onClick={(e) => {
                e.preventDefault();
                handleForgotPassword();
              }}
            >
              Glemt adgangskode?
            </a>
          </div>
        </form>
        <button className="ms-login-btn" onClick={handleLogin} disabled={isLoading}>
          <img
            src="https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg"
            alt="Microsoft Logo"
            className="ms-logo"
          />
          Login med Microsoft
        </button>
        <button onClick={() => googleLogin()} className="ms-login-btn" disabled={isLoading}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 48 48"
            width="20"
            height="20"
          >
            <path
              fill="#4285F4"
              d="M24 9.5c3.45 0 6.15 1.2 8.3 3.15l6.15-6.15C34.9 3.4 29.8 1 24 1 14.95 1 7.3 6.35 3.9 14.2l7.35 5.7C13.1 13.65 18 9.5 24 9.5z"
            />
            <path
              fill="#34A853"
              d="M46.8 24.55c0-1.4-.15-2.85-.45-4.2H24v8.35h12.95c-.6 3-2.3 5.6-4.95 7.3l7.55 5.85c4.35-4 6.25-9.75 6.25-15.3z"
            />
            <path
              fill="#FBBC05"
              d="M11.25 27.1C10.4 24.9 10 22.5 10 20s.4-4.9 1.25-7.1l-7.35-5.7C1.6 10.4 0 15 0 20s1.6 9.6 3.9 12.8l7.35-5.7z"
            />
            <path
              fill="#EA4335"
              d="M24 48c6.5 0 12-2.15 16-5.85l-7.55-5.85c-2.15 1.4-5 2.35-8.45 2.35-6 0-10.9-4.15-12.75-9.7l-7.35 5.7C7.3 41.65 14.95 48 24 48z"
            />
          </svg>
          Login med Google
        </button>
      </div>
    </div>
  );
}

export default Login;
